<script setup>
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';

import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Modal from '../../../../components/Modal.vue';
import { default as FluentIcon } from 'shared/components/FluentIcon/Icon.vue';
import dashboardIcons from 'shared/components/FluentIcon/dashboard-icons.json';
import { emitter } from 'shared/helpers/mitt';

import { usePipelinePermissions } from '../composables/usePipelinePermissions';
import { dismissMenuOnFocusOut } from '../helpers/menuDismissal';
import { normalizeLocale } from '../utils/localeFormatting';
import PipelineForm from './PipelineForm.vue';
import PipelinesHeader from './PipelinesHeader.vue';
import PipelineWelcome from './PipelineWelcome.vue';
import { REPORTING_DEFAULT_TIMEZONE } from 'dashboard/helper/reportingTimezone';
import {
  buildPipelineTemplates,
  getStageColorClasses,
  stageColorStyle,
} from './pipelineTemplates';

const emit = defineEmits(['switch-view']);
const { locale, t } = useI18n();
const store = useStore();
const { canManagePipeline } = usePipelinePermissions();

const pipelines = ref([]);
const isLoadingPipelines = ref(false);
const loadError = ref('');

const showDeleteModal = ref(false);
const showEditModal = ref(false);
const showWelcomeModal = ref(false);
const showNewPipelineModal = ref(false);
const editPipelineFormRef = ref(null);
const newPipelineFormRef = ref(null);
const pipelineToDelete = ref(null);
const pipelineToEdit = ref(null);

const isDeleting = ref(false);
const isFinishingEdit = ref(false);
const isHandlingCreate = ref(false);
const duplicatingPipelineId = ref('');

const openActionsPipelineId = ref('');
const actionMenuRef = ref(null);

const pipelineTemplates = computed(() => buildPipelineTemplates(t));
const accountPipelines = computed(
  () => store.getters['pipelines/getPipelines'] || []
);
const currentAccount = computed(() => store.getters.getCurrentAccount || {});
const currentAccountId = computed(
  () => store.getters.getCurrentAccountId ?? currentAccount.value.id ?? null
);
const normalizedAccountId = () =>
  currentAccountId.value === undefined || currentAccountId.value === null
    ? null
    : String(currentAccountId.value);
const accountGeneration = ref(0);
let pipelineFetchGeneration = 0;
const captureAccountContext = () => ({
  accountId: normalizedAccountId(),
  generation: accountGeneration.value,
});
const accountContextIsCurrent = context =>
  Boolean(context) &&
  context.generation === accountGeneration.value &&
  context.accountId === normalizedAccountId();
const reportingTimezone = computed(
  () =>
    currentAccount.value.reporting_timezone ||
    currentAccount.value.settings?.reporting_timezone ||
    REPORTING_DEFAULT_TIMEZONE
);

const normalizePipeline = pipeline => ({
  ...pipeline,
  stages: Object.fromEntries(
    Object.entries(pipeline.stages || {}).map(([id, stage]) => [
      id,
      {
        ...stage,
        id,
        position: Number.parseInt(stage.position, 10) || 0,
        message_templates: stage.message_templates || [],
      },
    ])
  ),
});

const remapStageReference = (stageId, stageIdMap) => {
  if (stageId === undefined || stageId === null || stageId === '') return null;
  return stageIdMap.get(String(stageId)) || null;
};

const duplicateSettings = (stageIdMap, settings = {}) => {
  const labelStageMappings = Array.isArray(settings.label_stage_mappings)
    ? settings.label_stage_mappings
        .map(mapping => {
          if (mapping.action !== 'move') return { ...mapping };
          const targetStage = remapStageReference(
            mapping.target_stage,
            stageIdMap
          );
          return targetStage ? { ...mapping, target_stage: targetStage } : null;
        })
        .filter(Boolean)
    : [];
  const stageLabelMappings = Array.isArray(settings.stage_label_mappings)
    ? settings.stage_label_mappings
        .map(mapping => {
          const stage = remapStageReference(mapping.stage, stageIdMap);
          return stage ? { ...mapping, stage } : null;
        })
        .filter(Boolean)
    : [];

  return {
    ...settings,
    label_stage_mappings: labelStageMappings,
    stage_label_mappings: stageLabelMappings,
    agents: Array.isArray(settings.agents) ? settings.agents : [],
  };
};

const fetchPipelines = async ({ showLoader = true } = {}) => {
  const context = captureAccountContext();
  pipelineFetchGeneration += 1;
  const requestGeneration = pipelineFetchGeneration;
  if (showLoader) isLoadingPipelines.value = true;
  loadError.value = '';

  try {
    await store.dispatch('pipelines/fetch', {
      force: true,
      accountId: context.accountId,
      generation: requestGeneration,
    });
    if (
      !accountContextIsCurrent(context) ||
      requestGeneration !== pipelineFetchGeneration
    ) {
      return false;
    }
    pipelines.value = accountPipelines.value.map(normalizePipeline);
    return true;
  } catch {
    if (
      accountContextIsCurrent(context) &&
      requestGeneration === pipelineFetchGeneration
    ) {
      loadError.value = t('PIPELINE.PIPELINES.MANAGER.LOAD_ERROR');
    }
    return false;
  } finally {
    if (
      showLoader &&
      accountContextIsCurrent(context) &&
      requestGeneration === pipelineFetchGeneration
    ) {
      isLoadingPipelines.value = false;
    }
  }
};

const handlePipelineCreated = async pipeline => {
  if (isHandlingCreate.value) return;
  const context = captureAccountContext();
  isHandlingCreate.value = true;

  if (pipeline?.id) {
    const normalizedPipeline = normalizePipeline(pipeline);
    const alreadyExists = pipelines.value.some(
      item => String(item.id) === String(normalizedPipeline.id)
    );

    if (!alreadyExists) {
      pipelines.value = [...pipelines.value, normalizedPipeline];
    }
  }

  await fetchPipelines({ showLoader: false });
  if (accountContextIsCurrent(context)) isHandlingCreate.value = false;
};

const closeDeleteModal = () => {
  if (isDeleting.value) return;
  showDeleteModal.value = false;
  pipelineToDelete.value = null;
};

const confirmDelete = pipeline => {
  if (!canManagePipeline.value || isDeleting.value) return;
  pipelineToDelete.value = pipeline;
  showDeleteModal.value = true;
};

const handleDelete = async () => {
  if (!pipelineToDelete.value || isDeleting.value) return;

  isDeleting.value = true;
  const deletedPipelineId = pipelineToDelete.value.id;
  const context = captureAccountContext();

  try {
    await store.dispatch('pipelines/deletePipeline', {
      id: deletedPipelineId,
      accountId: context.accountId,
    });
    if (!accountContextIsCurrent(context)) return;
    pipelines.value = pipelines.value.filter(
      pipeline => String(pipeline.id) !== String(deletedPipelineId)
    );

    emitter.emit('newToastMessage', {
      message: t('PIPELINE.PIPELINES.MANAGER.DELETE_SUCCESS'),
      action: { type: 'success' },
    });

    showDeleteModal.value = false;
    pipelineToDelete.value = null;
    await fetchPipelines({ showLoader: false });
  } catch (error) {
    if (!accountContextIsCurrent(context)) return;
    const errorMessage =
      error.response?.data?.error ||
      error.response?.data?.message ||
      t('PIPELINE.ERRORS.DELETE_PIPELINE_WITH_ITEMS');

    emitter.emit('newToastMessage', {
      message: errorMessage,
      action: { type: 'error' },
    });
  } finally {
    if (accountContextIsCurrent(context)) isDeleting.value = false;
  }
};

const closeEditModal = () => {
  if (isFinishingEdit.value) return;
  showEditModal.value = false;
  pipelineToEdit.value = null;
};

const requestEditModalClose = () => {
  if (isFinishingEdit.value) return false;
  return editPipelineFormRef.value?.requestClose?.() ?? true;
};

const startEdit = pipeline => {
  if (!canManagePipeline.value || isFinishingEdit.value) return;

  pipelineToEdit.value = {
    ...pipeline,
    id: String(pipeline.id),
    inbox_id: pipeline.inbox_id || null,
    stages: Object.fromEntries(
      Object.entries(pipeline.stages || {}).map(([key, stage]) => [
        String(key),
        {
          ...stage,
          id: String(stage.id || key),
        },
      ])
    ),
    settings: pipeline.settings || {},
    agents: pipeline.settings?.agents || [],
  };

  showEditModal.value = true;
};

const handleEdit = async () => {
  if (isFinishingEdit.value) return;
  const context = captureAccountContext();
  isFinishingEdit.value = true;

  await fetchPipelines({ showLoader: false });
  if (!accountContextIsCurrent(context)) return;

  emitter.emit('newToastMessage', {
    message: t('PIPELINE.PIPELINES.MANAGER.UPDATE_SUCCESS'),
    action: { type: 'success' },
  });

  showEditModal.value = false;
  pipelineToEdit.value = null;
  isFinishingEdit.value = false;
};

const duplicatePipeline = async pipeline => {
  if (!canManagePipeline.value || duplicatingPipelineId.value) return;

  const context = captureAccountContext();
  duplicatingPipelineId.value = String(pipeline.id);
  const timestamp = Date.now();

  try {
    const stageIdMap = new Map();
    const newStages = Object.fromEntries(
      Object.entries(pipeline.stages || {}).map(
        ([oldStageId, stage], stageIndex) => {
          const newId = `stage_${timestamp}_${stageIndex}`;
          stageIdMap.set(String(oldStageId), newId);
          if (stage.id !== undefined && stage.id !== null) {
            stageIdMap.set(String(stage.id), newId);
          }
          return [
            newId,
            {
              ...stage,
              id: newId,
              message_templates: (stage.message_templates || []).map(
                (messageTemplate, templateIndex) => ({
                  ...messageTemplate,
                  id: `template_${timestamp}_${stageIndex}_${templateIndex}`,
                  stage_id: newId,
                })
              ),
            },
          ];
        }
      )
    );

    await store.dispatch('pipelines/createPipeline', {
      accountId: context.accountId,
      name: t('PIPELINE.PIPELINES.MANAGER.COPY_NAME', {
        name: pipeline.name,
      }),
      description: pipeline.description,
      active: pipeline.active,
      stages: newStages,
      settings: duplicateSettings(stageIdMap, pipeline.settings),
    });
    if (!accountContextIsCurrent(context)) return;

    emitter.emit('newToastMessage', {
      message: t('PIPELINE.PIPELINES.MANAGER.DUPLICATE_SUCCESS'),
      action: { type: 'success' },
    });

    await fetchPipelines({ showLoader: false });
  } catch {
    if (!accountContextIsCurrent(context)) return;
    emitter.emit('newToastMessage', {
      message: t('PIPELINE.PIPELINES.MANAGER.DUPLICATE_ERROR'),
      action: { type: 'error' },
    });
  } finally {
    if (accountContextIsCurrent(context)) duplicatingPipelineId.value = '';
  }
};

const getSortedStages = (stages, settings = {}) => {
  if (!stages) return [];

  const enableWonLostStages = settings?.enable_won_lost_stages !== false;

  return Object.entries(stages)
    .map(([id, stage]) => ({
      id,
      ...stage,
      position: Number.parseInt(stage.position, 10) || 0,
    }))
    .filter(
      stage =>
        enableWonLostStages || (!stage.is_won_stage && !stage.is_lost_stage)
    )
    .sort(
      (firstStage, secondStage) => firstStage.position - secondStage.position
    );
};

const formatCreatedAt = value => {
  if (!value) return '';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  try {
    const formattedDate = new Intl.DateTimeFormat(
      normalizeLocale(locale.value),
      {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        timeZone: reportingTimezone.value,
      }
    ).format(date);

    return t('PIPELINE.PIPELINES.MANAGER.CREATED_AT', {
      date: formattedDate,
    });
  } catch {
    return '';
  }
};

const stageAccessibleLabel = stage => {
  const templateCount = stage.message_templates?.length || 0;
  if (!templateCount) return stage.name;

  return t('PIPELINE.PIPELINES.MANAGER.STAGE_WITH_TEMPLATES', {
    name: stage.name,
    count: templateCount,
  });
};

const pipelineDomId = pipeline =>
  String(pipeline.id).replace(/[^a-zA-Z0-9_-]/g, '-');

const setActionMenuRef = element => {
  actionMenuRef.value = element;
};

const closeActionMenu = () => {
  openActionsPipelineId.value = '';
  actionMenuRef.value = null;
};

// `v-on-clickaway` é registrado POR LINHA dentro do v-for, mas o menu aberto é
// um estado único do componente. Sem esta guarda, o clique que abria o menu da
// linha A era visto como "clique fora" pelos listeners das linhas B, C e D —
// que chamavam o mesmo `closeActionMenu` e fechavam o menu ~15ms depois de ele
// aparecer. Era isso que deixava Editar/Duplicar/Excluir inalcançáveis, tanto
// no mouse quanto no teclado (regressão 2026-08-05). Cada linha agora só fecha
// o menu se o menu aberto for o dela.
//
// O callback é memoizado por pipeline porque a diretiva re-registra o listener
// sempre que `binding.value` muda, e o re-registro reabre uma janela morta de
// um macrotask (`nextTick` interno) em que cliques fora são ignorados. Uma
// arrow inline no template mudaria de identidade a cada render e reabriria
// essa janela a cada atualização da lista.
const clickAwayHandlers = new Map();

const clickAwayFor = pipeline => {
  const pipelineId = String(pipeline.id);
  if (!clickAwayHandlers.has(pipelineId)) {
    clickAwayHandlers.set(pipelineId, () => {
      if (openActionsPipelineId.value === pipelineId) closeActionMenu();
    });
  }
  return clickAwayHandlers.get(pipelineId);
};

const focusActionTrigger = pipeline => {
  document
    .getElementById(`pipeline-actions-${pipelineDomId(pipeline)}`)
    ?.focus();
};

const toggleActionMenu = async pipeline => {
  const pipelineId = String(pipeline.id);
  if (openActionsPipelineId.value === pipelineId) {
    closeActionMenu();
    return;
  }

  openActionsPipelineId.value = pipelineId;
  await nextTick();
  actionMenuRef.value?.querySelector('[role="menuitem"]')?.focus();
};

const handleMenuKeydown = (event, pipeline) => {
  if (event.key === 'Escape') {
    event.preventDefault();
    closeActionMenu();
    focusActionTrigger(pipeline);
    return;
  }

  const menuItems = Array.from(
    event.currentTarget.querySelectorAll('[role="menuitem"]:not([disabled])')
  );
  if (!menuItems.length) return;

  const currentIndex = menuItems.indexOf(document.activeElement);
  const lastIndex = menuItems.length - 1;
  let nextIndex = currentIndex;

  if (event.key === 'ArrowDown')
    nextIndex = (currentIndex + 1) % menuItems.length;
  if (event.key === 'ArrowUp') {
    nextIndex = currentIndex <= 0 ? lastIndex : currentIndex - 1;
  }
  if (event.key === 'Home') nextIndex = 0;
  if (event.key === 'End') nextIndex = lastIndex;
  if (nextIndex === currentIndex) return;

  event.preventDefault();
  menuItems[nextIndex]?.focus();
};

const handleMenuFocusOut = event => {
  dismissMenuOnFocusOut(event, closeActionMenu);
};

const handlePipelineAction = (action, pipeline) => {
  closeActionMenu();

  if (action === 'edit') startEdit(pipeline);
  if (action === 'duplicate') duplicatePipeline(pipeline);
  if (action === 'delete') confirmDelete(pipeline);
};

const handleWelcomePipelineCreated = async (pipeline, accountContext) => {
  if (!accountContextIsCurrent(accountContext)) return;
  showWelcomeModal.value = false;
  await handlePipelineCreated(pipeline);
};

const handleOpenNewPipelineModal = () => {
  if (!canManagePipeline.value) return;
  showWelcomeModal.value = false;
  showNewPipelineModal.value = true;
};

const closeNewPipelineModal = () => {
  showNewPipelineModal.value = false;
};

const requestNewPipelineModalClose = () =>
  newPipelineFormRef.value?.requestClose?.() ?? true;

const handleNewPipelineSaved = async pipeline => {
  showNewPipelineModal.value = false;
  await handlePipelineCreated(pipeline);
};

const resetForAccountSwitch = () => {
  accountGeneration.value += 1;
  pipelineFetchGeneration += 1;
  clickAwayHandlers.clear();
  pipelines.value = [];
  loadError.value = '';
  isLoadingPipelines.value = false;
  showDeleteModal.value = false;
  showEditModal.value = false;
  showWelcomeModal.value = false;
  showNewPipelineModal.value = false;
  pipelineToDelete.value = null;
  pipelineToEdit.value = null;
  isDeleting.value = false;
  isFinishingEdit.value = false;
  isHandlingCreate.value = false;
  duplicatingPipelineId.value = '';
  closeActionMenu();
  store.dispatch('pipelines/reset');
};

watch(
  currentAccountId,
  (accountId, previousAccountId) => {
    if (String(accountId ?? '') === String(previousAccountId ?? '')) return;
    resetForAccountSwitch();
    fetchPipelines();
  },
  { flush: 'sync' }
);

onMounted(() => {
  emitter.on('open-new-pipeline-modal', handleOpenNewPipelineModal);
  fetchPipelines();
});

onUnmounted(() => {
  emitter.off('open-new-pipeline-modal', handleOpenNewPipelineModal);
});
</script>

<template>
  <div class="flex w-full flex-1 min-w-0 flex-col h-full bg-n-background">
    <PipelinesHeader
      :can-manage="canManagePipeline"
      @switch-view="view => emit('switch-view', view)"
      @pipeline-created="handlePipelineCreated"
    />

    <section
      class="flex-1 min-h-0 overflow-y-auto"
      :aria-label="t('PIPELINE.PIPELINES.MANAGER.CONTENT_LABEL')"
      :aria-busy="isLoadingPipelines"
      aria-live="polite"
    >
      <div
        v-if="loadError"
        role="alert"
        class="flex flex-col gap-3 p-4 mx-4 mt-4 border rounded-xl border-n-ruby-6 bg-n-ruby-3 sm:flex-row sm:items-center sm:justify-between sm:mx-6"
      >
        <div class="flex items-start gap-3 text-sm text-n-ruby-11">
          <span class="i-lucide-circle-alert flex-shrink-0 mt-0.5" />
          <span>{{ loadError }}</span>
        </div>
        <Button
          type="button"
          variant="outline"
          color="ruby"
          size="lg"
          icon="i-lucide-refresh-cw"
          :disabled="isLoadingPipelines"
          :is-loading="isLoadingPipelines"
          data-test-id="retry-pipelines"
          @click="fetchPipelines"
        >
          {{ t('PIPELINE.PIPELINES.MANAGER.RETRY') }}
        </Button>
      </div>

      <div
        v-if="isLoadingPipelines && !pipelines.length"
        role="status"
        class="flex flex-col items-center justify-center gap-3 px-4 py-20 text-n-slate-10"
      >
        <Spinner :size="32" aria-hidden="true" />
        <p class="text-sm">{{ t('PIPELINE.PIPELINES.MANAGER.LOADING') }}</p>
      </div>

      <div
        v-else-if="!pipelines.length && !loadError"
        class="flex flex-col items-center justify-center px-5 py-16 text-center sm:py-24"
      >
        <div
          class="flex items-center justify-center w-16 h-16 rounded-2xl bg-n-brand/10 text-n-brand"
          aria-hidden="true"
        >
          <FluentIcon icon="briefcase" size="28" :icons="dashboardIcons" />
        </div>
        <h2 class="mt-5 text-lg font-semibold text-n-slate-12">
          {{ t('PIPELINE.PIPELINES.MANAGER.EMPTY_TITLE') }}
        </h2>
        <p class="max-w-md mt-2 text-sm leading-6 text-n-slate-10">
          {{ t('PIPELINE.PIPELINES.MANAGER.EMPTY_DESCRIPTION') }}
        </p>
        <div
          v-if="canManagePipeline"
          class="flex flex-col w-full max-w-sm gap-2 mt-6 sm:flex-row sm:justify-center"
        >
          <Button
            type="button"
            variant="solid"
            color="blue"
            size="lg"
            icon="i-lucide-plus"
            @click="handleOpenNewPipelineModal"
          >
            {{ t('PIPELINE.PIPELINES.ACTIONS.NEW') }}
          </Button>
          <Button
            type="button"
            variant="outline"
            color="slate"
            size="lg"
            icon="i-lucide-layout-template"
            @click="showWelcomeModal = true"
          >
            {{ t('PIPELINE.TEMPLATES_LIBRARY.TRIGGER_LABEL') }}
          </Button>
        </div>
      </div>

      <div v-else-if="pipelines.length" class="p-4 space-y-3 sm:p-6">
        <article
          v-for="pipeline in pipelines"
          :key="pipeline.id"
          class="p-4 border rounded-xl border-n-weak bg-n-solid-2 transition-colors hover:border-n-strong sm:p-5"
          :aria-labelledby="`pipeline-title-${pipelineDomId(pipeline)}`"
        >
          <div class="flex items-start justify-between gap-3">
            <div class="flex min-w-0 gap-3">
              <div
                class="flex items-center justify-center flex-shrink-0 w-10 h-10 rounded-xl"
                :class="
                  pipeline.active
                    ? 'bg-n-brand/10 text-n-brand'
                    : 'bg-n-alpha-2 text-n-slate-10'
                "
                aria-hidden="true"
              >
                <FluentIcon
                  icon="briefcase"
                  size="19"
                  :icons="dashboardIcons"
                />
              </div>

              <div class="min-w-0">
                <div class="flex flex-wrap items-center gap-2">
                  <h2
                    :id="`pipeline-title-${pipelineDomId(pipeline)}`"
                    class="text-base font-semibold leading-6 truncate text-n-slate-12"
                  >
                    {{ pipeline.name }}
                  </h2>
                  <span
                    class="inline-flex items-center gap-1.5 px-2 py-1 text-xs font-medium rounded-md"
                    :class="
                      pipeline.active
                        ? 'bg-n-teal-3 text-n-teal-11'
                        : 'bg-n-alpha-2 text-n-slate-10'
                    "
                  >
                    <span
                      class="w-1.5 h-1.5 rounded-full"
                      :class="pipeline.active ? 'bg-n-teal-9' : 'bg-n-slate-8'"
                      aria-hidden="true"
                    />
                    {{
                      pipeline.active
                        ? t('PIPELINE.PIPELINES.MANAGER.STATUS_ACTIVE')
                        : t('PIPELINE.PIPELINES.MANAGER.STATUS_INACTIVE')
                    }}
                  </span>
                </div>

                <p
                  v-if="pipeline.description"
                  class="mt-1 text-sm leading-5 text-n-slate-10"
                >
                  {{ pipeline.description }}
                </p>

                <div class="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2">
                  <span
                    v-if="formatCreatedAt(pipeline.created_at)"
                    class="text-xs text-n-slate-10"
                  >
                    {{ formatCreatedAt(pipeline.created_at) }}
                  </span>
                  <span
                    class="inline-flex items-center gap-1.5 text-xs text-n-slate-10"
                  >
                    <span
                      class="i-lucide-columns-3 size-3.5"
                      aria-hidden="true"
                    />
                    {{
                      t('PIPELINE.PIPELINES.MANAGER.STAGE_COUNT', {
                        count: getSortedStages(
                          pipeline.stages,
                          pipeline.settings
                        ).length,
                      })
                    }}
                  </span>
                  <span
                    v-if="pipeline.settings?.agents?.length"
                    class="inline-flex items-center gap-1.5 text-xs text-n-slate-10"
                  >
                    <span class="i-lucide-users size-3.5" aria-hidden="true" />
                    {{
                      t('PIPELINE.PIPELINES.MANAGER.AGENT_COUNT', {
                        count: pipeline.settings.agents.length,
                      })
                    }}
                  </span>
                </div>
              </div>
            </div>

            <div
              v-if="canManagePipeline"
              v-on-clickaway="clickAwayFor(pipeline)"
              class="relative flex-shrink-0"
            >
              <Button
                :id="`pipeline-actions-${pipelineDomId(pipeline)}`"
                type="button"
                variant="ghost"
                color="slate"
                size="lg"
                icon="i-lucide-ellipsis-vertical"
                :is-loading="duplicatingPipelineId === String(pipeline.id)"
                :disabled="Boolean(duplicatingPipelineId) || isDeleting"
                :aria-label="
                  t('PIPELINE.PIPELINES.MANAGER.ACTIONS_LABEL', {
                    name: pipeline.name,
                  })
                "
                :aria-expanded="openActionsPipelineId === String(pipeline.id)"
                :aria-controls="`pipeline-actions-menu-${pipelineDomId(pipeline)}`"
                aria-haspopup="menu"
                @click="toggleActionMenu(pipeline)"
              />

              <div
                v-if="openActionsPipelineId === String(pipeline.id)"
                :id="`pipeline-actions-menu-${pipelineDomId(pipeline)}`"
                :ref="setActionMenuRef"
                role="menu"
                :aria-labelledby="`pipeline-actions-${pipelineDomId(pipeline)}`"
                class="absolute z-50 w-52 p-1.5 mt-1 border shadow-lg ltr:right-0 rtl:left-0 top-full rounded-xl border-n-container bg-n-alpha-3 backdrop-blur-[100px]"
                @keydown="handleMenuKeydown($event, pipeline)"
                @focusout="handleMenuFocusOut"
              >
                <button
                  type="button"
                  role="menuitem"
                  class="flex items-center w-full min-h-11 gap-3 px-3 py-2 text-sm text-left rounded-lg text-n-slate-12 outline-none hover:bg-n-alpha-2 focus-visible:bg-n-alpha-2 focus-visible:ring-2 focus-visible:ring-n-brand"
                  @click="handlePipelineAction('edit', pipeline)"
                >
                  <span class="i-lucide-pencil size-4" aria-hidden="true" />
                  {{ t('PIPELINE.PIPELINES.MANAGER.EDIT_ACTION') }}
                </button>
                <button
                  type="button"
                  role="menuitem"
                  class="flex items-center w-full min-h-11 gap-3 px-3 py-2 text-sm text-left rounded-lg text-n-slate-12 outline-none hover:bg-n-alpha-2 focus-visible:bg-n-alpha-2 focus-visible:ring-2 focus-visible:ring-n-brand"
                  @click="handlePipelineAction('duplicate', pipeline)"
                >
                  <span class="i-lucide-copy size-4" aria-hidden="true" />
                  {{ t('PIPELINE.PIPELINES.MANAGER.DUPLICATE_ACTION') }}
                </button>
                <button
                  type="button"
                  role="menuitem"
                  class="flex items-center w-full min-h-11 gap-3 px-3 py-2 text-sm text-left rounded-lg text-n-ruby-11 outline-none hover:bg-n-ruby-3 focus-visible:bg-n-ruby-3 focus-visible:ring-2 focus-visible:ring-n-ruby-9"
                  @click="handlePipelineAction('delete', pipeline)"
                >
                  <span class="i-lucide-trash-2 size-4" aria-hidden="true" />
                  {{ t('PIPELINE.PIPELINES.MANAGER.DELETE_ACTION') }}
                </button>
              </div>
            </div>
          </div>

          <div
            class="flex items-center gap-1.5 pb-2 mt-4 overflow-x-auto overscroll-x-contain"
            role="list"
            :aria-label="
              t('PIPELINE.PIPELINES.MANAGER.STAGES_LABEL', {
                name: pipeline.name,
              })
            "
          >
            <template
              v-if="getSortedStages(pipeline.stages, pipeline.settings).length"
            >
              <template
                v-for="(stage, index) in getSortedStages(
                  pipeline.stages,
                  pipeline.settings
                )"
                :key="stage.id"
              >
                <span
                  v-if="index > 0"
                  class="i-lucide-chevron-right flex-shrink-0 size-3.5 text-n-slate-8"
                  aria-hidden="true"
                />
                <div
                  role="listitem"
                  :aria-label="stageAccessibleLabel(stage)"
                  class="inline-flex items-center flex-shrink-0 min-h-9 gap-2 px-3 py-1.5 text-xs font-medium border rounded-lg"
                  :class="getStageColorClasses(stage.color).badge"
                  :style="stageColorStyle(stage.color, 'badge')"
                >
                  <span
                    class="w-2 h-2 rounded-full"
                    :class="getStageColorClasses(stage.color).dot"
                    :style="stageColorStyle(stage.color, 'dot')"
                    aria-hidden="true"
                  />
                  <span class="whitespace-nowrap">{{ stage.name }}</span>
                  <span
                    v-if="stage.message_templates?.length"
                    class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-n-alpha-2"
                    aria-hidden="true"
                  >
                    <span class="i-lucide-message-square-text size-3" />
                    {{ stage.message_templates.length }}
                  </span>
                </div>
              </template>
            </template>
            <span v-else role="listitem" class="text-xs text-n-slate-10">
              {{ t('PIPELINE.PIPELINES.MANAGER.NO_STAGES') }}
            </span>
          </div>

          <div
            v-if="pipeline.settings?.agents?.length"
            class="flex items-center min-w-0 gap-3 pt-3 mt-2 border-t border-n-weak"
            :aria-label="
              t('PIPELINE.PIPELINES.MANAGER.AGENTS_LABEL', {
                name: pipeline.name,
              })
            "
          >
            <div class="flex flex-shrink-0 -space-x-1.5" aria-hidden="true">
              <Avatar
                v-for="agent in pipeline.settings.agents.slice(0, 5)"
                :key="agent.id"
                :name="agent.name || ''"
                :src="agent.thumbnail || ''"
                :size="24"
                class="ring-2 ring-n-solid-2"
              />
            </div>
            <span class="min-w-0 text-xs truncate text-n-slate-10">
              {{
                pipeline.settings.agents
                  .map(agent => agent.name)
                  .filter(Boolean)
                  .join(', ')
              }}
            </span>
          </div>
        </article>
      </div>
    </section>

    <Modal
      v-model:show="showEditModal"
      size="full"
      :show-close-button="!isFinishingEdit"
      :close-on-backdrop-click="!isFinishingEdit"
      :close-on-escape="!isFinishingEdit"
      :before-close="requestEditModalClose"
      @close="closeEditModal"
    >
      <div class="p-5 sm:p-8">
        <h2 data-modal-title class="mb-4 text-xl font-semibold text-n-slate-12">
          {{ t('PIPELINE.PIPELINES.EDIT.TITLE') }}
        </h2>
        <PipelineForm
          v-if="pipelineToEdit"
          ref="editPipelineFormRef"
          is-editing
          :initial-data="pipelineToEdit"
          @saved="handleEdit"
          @close="closeEditModal"
        />
      </div>
    </Modal>

    <Modal
      v-model:show="showDeleteModal"
      :show-close-button="!isDeleting"
      :close-on-backdrop-click="!isDeleting"
      :close-on-escape="!isDeleting"
      initial-focus-selector="[data-delete-cancel]"
      @close="closeDeleteModal"
    >
      <div class="p-5 sm:p-6">
        <div
          class="flex items-center justify-center w-12 h-12 mb-4 rounded-full bg-n-ruby-3 text-n-ruby-11"
          aria-hidden="true"
        >
          <span class="i-lucide-trash-2 size-5" />
        </div>
        <h2 data-modal-title class="text-xl font-semibold text-n-slate-12">
          {{ t('PIPELINE.PIPELINES.DELETE.TITLE') }}
        </h2>
        <p class="mt-3 text-sm leading-6 text-n-slate-11">
          {{
            t('PIPELINE.PIPELINES.DELETE.DESCRIPTION', {
              name: pipelineToDelete?.name,
            })
          }}
        </p>
        <p class="mt-2 text-sm leading-6 text-n-ruby-11">
          {{ t('PIPELINE.PIPELINES.MANAGER.DELETE_WARNING') }}
        </p>
        <div
          class="flex flex-col-reverse gap-2 mt-6 sm:flex-row sm:justify-end"
        >
          <Button
            type="button"
            variant="ghost"
            color="slate"
            size="lg"
            :disabled="isDeleting"
            data-delete-cancel
            @click="closeDeleteModal"
          >
            {{ t('PIPELINE.CANCEL') }}
          </Button>
          <Button
            type="button"
            variant="solid"
            color="ruby"
            size="lg"
            :is-loading="isDeleting"
            :disabled="isDeleting"
            data-test-id="confirm-delete-pipeline"
            @click="handleDelete"
          >
            {{ t('PIPELINE.DELETE') }}
          </Button>
        </div>
      </div>
    </Modal>

    <PipelineWelcome
      :show="showWelcomeModal"
      :templates="pipelineTemplates"
      :account-id="currentAccountId"
      :account-generation="accountGeneration"
      @close="showWelcomeModal = false"
      @start-from-scratch="handleOpenNewPipelineModal"
      @pipeline-created="handleWelcomePipelineCreated"
    />

    <Modal
      v-model:show="showNewPipelineModal"
      size="full"
      :before-close="requestNewPipelineModalClose"
      @close="closeNewPipelineModal"
    >
      <div class="p-5 sm:p-8">
        <h2 data-modal-title class="mb-4 text-xl font-semibold text-n-slate-12">
          {{ t('PIPELINE.PIPELINES.ACTIONS.NEW') }}
        </h2>
        <PipelineForm
          ref="newPipelineFormRef"
          :initial-data="{
            name: '',
            description: '',
            stages: {},
            settings: { agents: [] },
          }"
          @saved="handleNewPipelineSaved"
          @close="closeNewPipelineModal"
        />
      </div>
    </Modal>
  </div>
</template>
